import React, { useState, useEffect, createRef, createContext } from "react";
import { useCookies } from "react-cookie";
import { useSelector } from "react-redux";
import useAuthAction from "../../custom-hooks/useAuthAction";
import Logout from "../../pages/shared/Logout/logout";
import {
	TOKEN_HOLDER,
	DISPLAY_CSAT_MODAL,
	USER_NAME_HOLDER,
	USER_ROLE_HOLDER
} from "../../utils/constants";
import { Banner, GlobalMenu, PageLoader, SideMenu } from "../index";
import "./layout.css";
import { useApiGet } from "../../api/apiCall";
import { getStudentProfileUrl } from "../../api/urls";
import FeedbackModal from "../feedbackModal/feedback";

export const parent = createRef(null);

export const StudentProfileContext = createContext();

let menuItems = [];

const studentPaths = {
	dashboard: "/dashboard",
	profile: "/profile",
	acceptance: "/acceptance",
	"course registration": "/course_registration",
	"school fees": "/school_fees",
	results: "/results",
	sundry: "/sundry",
	application: "/application",
	referral: "/referral",
	hostel: "/hostel",
	"campus exit": "/campus_exit"
};

const applicantPaths = {
	application: "/application"
};

const staffPaths = {
	dashboard: "/dashboard",
	profile: "/profile",
	applications: "/applications/reset",
	referral: "/referral",
	"approve courses": "/approve_courses",
	"course management": "/course_management/manage_course",
	"fees assignment": "/fees_assignment/school_fees",
	"fees approval": "/fees_approval/school_fees",
	"campus exit": "/campus_exit/exit_requests",
	"invoice management": "/invoice_management/invoice",
	"lecturer management": "/lecturer_management/lecturer_upload",
	"notice board setup": "/notice_board_management",
	"results & class list": "/records",
	reports: "/reports/application_payment_reports",
	"result management": "/results/classlist",
	"student management": "/student_management/search_student",
	"user management": "/user_management/users",
	"hostel management": "/hostel_management/manage_hostel",
	"school setup": "/school_setup/manage_faculty_and_department"
};

const Layout = ({ children, title, noHeader }) => {
	const [open, setOpen] = useState(false);
	const [signOutModal, setSignOutModal] = useState(false);
	const [cookies] = useCookies([
		TOKEN_HOLDER,
		DISPLAY_CSAT_MODAL,
		USER_NAME_HOLDER,
		USER_ROLE_HOLDER
	]);

	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(
		cookies[DISPLAY_CSAT_MODAL]
			? JSON.parse(cookies[DISPLAY_CSAT_MODAL])
			: false
	);

	useEffect(() => {
		cookies[DISPLAY_CSAT_MODAL] &&
			setIsFeedbackModalOpen(JSON.parse(cookies[DISPLAY_CSAT_MODAL]));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cookies[DISPLAY_CSAT_MODAL]]);

	const { data, isLoading, error } = useApiGet(
		getStudentProfileUrl({ refCode: false }),
		{
			refetchOnWindowFocus: false,
			enabled: cookies[USER_ROLE_HOLDER] === "student"
		}
	);

	const menuItemsFromApi = useSelector((state) => state.menuItemsData);
	const { tokenExpirationDate, impersonatorUsername, isImpersonating } =
		useSelector((state) => state.impersonatorDetails);
	const { logout } = useAuthAction();

	const { [USER_ROLE_HOLDER]: userRole, [USER_NAME_HOLDER]: userName } =
		cookies;
	switch (userRole) {
		// Original implementation
		// case "student":
		// 	menuItems = menuItemsFromApi
		// 		.map((item) => ({
		// 			name: item,
		// 			path: studentPaths[item.toLowerCase()]
		// 		}))
		// 		.filter((item) => item.path);
		// 	break;

		// Temp implementation
		case "student":
			menuItems = [
				...menuItemsFromApi,
				...(menuItemsFromApi.some(
					(item) => item.toLowerCase() === "campus exit"
				)
					? []
					: ["Campus Exit"])
			]
				.map((item) => ({
					name: item,
					path: studentPaths[item.toLowerCase()]
				}))
				.filter((item) => item.path);
			break;
		case "applicant":
			menuItems = menuItemsFromApi
				.map((item) => ({
					name: item,
					path: applicantPaths[item.toLowerCase()]
				}))
				.filter((item) => item.path);
			break;
		default:
			menuItems = menuItemsFromApi
				.map((item) => ({
					name: item,
					path: staffPaths[item.toLowerCase()]
				}))
				.filter((item) => item.path);
			break;
	}

	if (isLoading) {
		return <PageLoader />;
	}

	if (error) {
		return "An error has occurred: " + error?.message;
	}

	return (
		<StudentProfileContext.Provider value={{ profileData: data?.data }}>
			<div className="app-container">
				<section className="res-header">
					{isImpersonating && (
						<Banner
							impersonator={impersonatorUsername}
							impersonated={userName}
							date={tokenExpirationDate}
							onEndSessionClick={() => logout()}
						/>
					)}
					{isFeedbackModalOpen && (
						<FeedbackModal
							isOpen={isFeedbackModalOpen}
							onClose={() => setIsFeedbackModalOpen(false)}
						/>
					)}
					<GlobalMenu
						title={title}
						openSide={() => setOpen(true)}
						setSignOutModal={setSignOutModal}
						userName={userName}
						userRole={userRole}
					/>
				</section>
				<section className={`${open ? "res-side" : "res-no-side"}`}>
					<div
						className="res-side-menu--background"
						onClick={() => setOpen(false)}
					></div>
					<SideMenu
						paths={menuItems}
						closeSide={() => setOpen(false)}
						setSignOutModal={setSignOutModal}
					/>
				</section>
				<div
					className={`children ${noHeader ? "no-header-tab" : ""}`}
					style={isImpersonating ? { paddingTop: "96px" } : {}}
					ref={parent}
				>
					{children}
				</div>
				<Logout
					isOpen={signOutModal}
					closeModal={() => setSignOutModal(false)}
				/>
			</div>
		</StudentProfileContext.Provider>
	);
};

export { Layout };
